import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Brain, 
  Briefcase, 
  GraduationCap, 
  Users, 
  Zap, 
  Target,
  ArrowRight,
  Check,
  X,
  Star,
  Clock,
  Award,
  TrendingUp,
  Send,
  Loader,
  MessageCircle
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= NAVBAR =============
const Navbar = ({ onEnroll }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-[#0a0f1a]/90 border-b border-teal-500/10' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <motion.div className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </motion.div>

          <div className="hidden md:flex items-center gap-12">
            <a href="/" className="text-sm text-gray-300 hover:text-teal-400 transition">Home</a>
            <a href="#programs" className="text-sm text-gray-300 hover:text-teal-400 transition">Programs</a>
            <a href="#why-us" className="text-sm text-gray-300 hover:text-teal-400 transition">Why Us</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onEnroll}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Enroll Now
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-80 h-80 bg-cyan-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-80 h-80 bg-teal-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-80 h-80 bg-emerald-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onConsultation }) => {
  return (
    <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden pt-20 bg-[#0a0f1a]">
      <GradientOrbs />

      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.15),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div
          className="mb-6 inline-block"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 backdrop-blur-sm">
            ✨ Transform Your Career with AI
          </span>
        </motion.div>

        <motion.h1
          className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="block text-white">Master AI Skills</span>
          <span className="block bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Build Your Future
          </span>
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Expert-led training programs from beginner to advanced. Learn AI, machine learning, and cutting-edge technologies that power the future.
        </motion.p>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {[
            { number: "5,000+", label: "Students Trained" },
            { number: "92%", label: "Success Rate" },
            { number: "4.9/5", label: "Average Rating" },
            { number: "50+", label: "Expert Mentors" }
          ].map((stat, index) => (
            <div key={index} className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-4 hover:border-teal-500/20 transition-all duration-300">
              <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent mb-1">
                {stat.number}
              </div>
              <div className="text-xs md:text-sm text-gray-400">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(20, 184, 166, 0.3)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('programs').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold shadow-[0_0_30px_rgba(20,184,166,0.2)] transition flex items-center gap-2"
          >
            Explore Programs <ArrowRight size={18} />
          </motion.button>
          <motion.button
            onClick={onConsultation}
            whileHover={{ scale: 1.05, borderColor: "rgba(20, 184, 166, 0.5)" }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-500/40 flex items-center gap-2"
          >
            <MessageCircle size={18} />
            Book Free Consultation
          </motion.button>
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= PROGRAM CARD =============
const ProgramCard = ({ program, index, onEnroll }) => {
  const [isHovered, setIsHovered] = useState(false);

  const icons = {
    'brain': Brain,
    'briefcase': Briefcase,
    'graduation': GraduationCap,
    'users': Users,
    'zap': Zap,
    'target': Target
  };

  const Icon = icons[program.icon] || Brain;

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-100px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-emerald-500/10 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 hover:border-teal-500/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        {program.popular && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 rounded-full text-xs font-bold text-white flex items-center gap-1">
            <Star size={12} fill="currentColor" />
            POPULAR
          </div>
        )}

        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(rgba(20, 184, 166, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(20, 184, 166, 0.3) 1px, transparent 1px)`,
          backgroundSize: '20px 20px'
        }}></div>

        <div className="relative z-10 flex flex-col h-full">
          <motion.div
            className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-teal-500/20 group-hover:from-cyan-500/30 group-hover:to-emerald-500/30 transition-all duration-500"
            animate={isHovered ? { rotate: [0, -10, 10, -10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <Icon className="text-teal-300 group-hover:text-teal-200 transition" size={32} />
          </motion.div>

          <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-cyan-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all duration-300">
            {program.title}
          </h3>
          
          <div className="flex items-center gap-2 mb-4">
            <Clock size={16} className="text-teal-400" />
            <span className="text-sm text-teal-400 font-semibold">{program.duration}</span>
          </div>

          <p className="text-gray-400 mb-6 flex-grow leading-relaxed">
            {program.description}
          </p>

          <ul className="space-y-3 mb-8">
            {program.features.map((feature, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-400 flex items-start gap-3"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i }}
              >
                <Check size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>{feature}</span>
              </motion.li>
            ))}
          </ul>

          {/* Two buttons: Know More (if has link) + Enroll Now */}
          <div className="flex gap-3 mt-auto">
            {program.link && (
              <Link to={program.link} className="flex-1">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-3 border border-teal-500/30 rounded-full text-teal-400 font-semibold transition hover:bg-teal-500/10 flex items-center justify-center gap-2"
                >
                  Know More <ArrowRight size={16} />
                </motion.button>
              </Link>
            )}
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(20, 184, 166, 0.3)" }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onEnroll(program)}
              className={`${program.link ? 'flex-1' : 'w-full'} py-3 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold transition`}
            >
              Enroll Now
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============= PROGRAMS SECTION =============
const ProgramsSection = ({ onEnroll }) => {
  const programs = [
    {
      icon: 'brain',
      title: "AI Fundamentals",
      duration: "7-Day Bootcamp",
      description: "Perfect for beginners. Learn AI basics, machine learning concepts, and get hands-on with real projects.",
      link: "/ai-fundamentals",
      features: [
        "Zero coding prerequisites",
        "Live interactive sessions",
        "Real-world case studies",
        "Industry certification",
        "30-day support access"
      ],
      popular: true,
      level: "Beginner"
    },
    {
      icon: 'briefcase',
      title: "Corporate AI Training",
      duration: "14-Day Program",
      description: "Advanced training designed for professionals looking to implement AI solutions in corporate environments.",
      link: "/corporate-ai-training",
      features: [
        "Industry-specific modules",
        "ROI-focused projects",
        "Team collaboration tools",
        "Executive certification",
        "Professional network access"
      ],
      level: "Professional"
    },
    {
      icon: 'graduation',
      title: "AI Fellowship",
      duration: "6-Month Deep Dive",
      description: "Elite program with direct mentorship. Build production-ready AI systems and launch your career.",
      link: "/ai-fellowship",
      features: [
        "1-on-1 expert mentoring",
        "Live client projects",
        "Specialized tracks (NLP, CV, etc.)",
        "Job placement assistance",
        "Lifetime alumni network"
      ],
      popular: true,
      level: "Advanced"
    },
    {
      icon: 'users',
      title: "Student Program",
      duration: "21-Day Course",
      description: "Special program for students with discounted rates. Build your AI portfolio and kickstart your career.",
      link: "/student-program",
      features: [
        "Academic-friendly schedule",
        "Career guidance sessions",
        "Student pricing (50% off)",
        "Peer learning groups",
        "Internship opportunities"
      ],
      level: "Student"
    },
    {
      icon: 'target',
      title: "Prompt Engineering",
      duration: "5-Day Workshop",
      description: "Master the art of working with AI. Learn advanced techniques to get the best results from AI tools.",
      link: "/prompt-engineering",
      features: [
        "Advanced prompt techniques",
        "Multi-modal AI mastery",
        "Automation workflows",
        "Professional templates",
        "Certification included"
      ],
      level: "All Levels"
    },
    {
      icon: 'zap',
      title: "AI Project Accelerator",
      duration: "30-Day Sprint",
      description: "Build and deploy a complete AI project from scratch with expert guidance every step of the way.",
      enrollOnly: true,
      features: [
        "End-to-end project build",
        "Deployment & scaling",
        "Code reviews & feedback",
        "Portfolio piece",
        "Launch support"
      ],
      level: "Intermediate"
    }
  ];

  return (
    <section id="programs" className="relative py-24 px-4 overflow-hidden bg-[#0a0f1a]">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[300px] bg-gradient-to-b from-cyan-950/20 to-transparent" />
      <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-teal-500/[0.03] rounded-full blur-[120px]" />
      <div className="absolute bottom-20 left-0 w-[300px] h-[300px] bg-cyan-600/[0.02] rounded-full blur-[100px]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            Programs
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Our Training Programs
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            From beginners to experts, we have the perfect program to accelerate your AI journey
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {programs.map((program, index) => (
            <ProgramCard key={index} program={program} index={index} onEnroll={onEnroll} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= WHY US SECTION =============
const WhyUsSection = () => {
  const benefits = [
    {
      icon: Award,
      title: "Industry Experts",
      description: "Learn from professionals with 10+ years of AI experience at top tech companies"
    },
    {
      icon: TrendingUp,
      title: "Career Growth",
      description: "92% of our graduates report career advancement within 6 months"
    },
    {
      icon: Users,
      title: "Community Support",
      description: "Join a thriving community of 5,000+ AI enthusiasts and professionals"
    },
    {
      icon: Target,
      title: "Hands-On Learning",
      description: "Build real projects and gain practical experience with cutting-edge tools"
    }
  ];

  return (
    <section id="why-us" className="relative py-24 px-4 overflow-hidden bg-[#080c14]">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-teal-500/[0.03] rounded-full blur-[150px]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            Why Us
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Why Choose{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400">AI Factory?</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            We're committed to your success with proven results and industry recognition
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300 group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
            >
              <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-teal-500/20 group-hover:from-cyan-500/30 group-hover:to-emerald-500/30 transition-all">
                <benefit.icon className="text-teal-300 group-hover:text-teal-200 transition" size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-cyan-400 group-hover:to-teal-400 transition-all">{benefit.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= ENROLLMENT MODAL WITH EMAIL =============
const EnrollmentModal = ({ isOpen, onClose, program }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    selectedProgram: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  const programsList = [
    { title: 'AI Fundamentals', duration: '7-Day Bootcamp' },
    { title: 'Corporate AI Training', duration: '14-Day Program' },
    { title: 'AI Fellowship', duration: '6-Month Deep Dive' },
    { title: 'Student Program', duration: '21-Day Course' },
    { title: 'Prompt Engineering', duration: '5-Day Workshop' },
    { title: 'AI Project Accelerator', duration: '30-Day Sprint' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const programTitle = program?.title || formData.selectedProgram;
    
    if (!formData.name || !formData.email || !formData.phone || !formData.experience || (!program && !formData.selectedProgram)) {
      setStatus({ type: 'error', message: 'Please fill in all fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          interest: programTitle,
          message: `New Enrollment Request\n\nProgram: ${programTitle}\nExperience Level: ${formData.experience}\n\nPlease contact me to complete the enrollment.`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Enrollment submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', experience: '', selectedProgram: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', experience: '', selectedProgram: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-[#0a0f1a] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Enrollment Submitted!</h3>
            <p className="text-gray-400 mb-6">
              Thank you for enrolling. We'll contact you within 24 hours with next steps.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <Zap size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">{program?.duration || 'AI Training'}</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Enroll Now</h2>
            <p className="text-teal-400 mb-6">{program?.title || 'Choose your program below'}</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              {/* Program Selector - only show if no program pre-selected */}
              {!program && (
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Select Program *</label>
                  <select
                    value={formData.selectedProgram}
                    onChange={(e) => handleChange('selectedProgram', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-[#0a0f1a]">Choose a program</option>
                    {programsList.map((p, i) => (
                      <option key={i} value={p.title} className="bg-[#0a0f1a]">
                        {p.title} ({p.duration})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Experience Level *</label>
                <select
                  value={formData.experience}
                  onChange={(e) => handleChange('experience', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-[#0a0f1a]">Select experience level</option>
                  <option value="Beginner - No AI experience" className="bg-[#0a0f1a]">Beginner - No AI experience</option>
                  <option value="Intermediate - Some AI tools" className="bg-[#0a0f1a]">Intermediate - Some AI tools</option>
                  <option value="Advanced - Multiple platforms" className="bg-[#0a0f1a]">Advanced - Multiple platforms</option>
                  <option value="Expert - AI Professional" className="bg-[#0a0f1a]">Expert - AI Professional</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Enrollment
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= CONSULTATION MODAL WITH EMAIL =============
const ConsultationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          interest: formData.interest || 'Free Consultation',
          message: formData.message || 'I would like to schedule a free consultation to learn more about your AI training programs.'
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Consultation request submitted!' });
        setFormData({ name: '', email: '', phone: '', interest: '', message: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Consultation error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', interest: '', message: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-[#0a0f1a] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Request Submitted!</h3>
            <p className="text-gray-400 mb-6">
              Thank you for your interest! Our team will contact you within 24 hours to schedule your free consultation.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <MessageCircle size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">Free Session</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Book Free Consultation</h2>
            <p className="text-gray-400 mb-6">Get personalized guidance for your AI learning journey</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Interested In</label>
                <select
                  value={formData.interest}
                  onChange={(e) => handleChange('interest', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-[#0a0f1a]">Select a program</option>
                  <option value="AI Fundamentals" className="bg-[#0a0f1a]">AI Fundamentals (7-Day)</option>
                  <option value="Corporate AI Training" className="bg-[#0a0f1a]">Corporate AI Training (14-Day)</option>
                  <option value="AI Fellowship" className="bg-[#0a0f1a]">AI Fellowship (6-Month)</option>
                  <option value="Student Program" className="bg-[#0a0f1a]">Student Program (21-Day)</option>
                  <option value="Prompt Engineering" className="bg-[#0a0f1a]">Prompt Engineering (5-Day)</option>
                  <option value="AI Project Accelerator" className="bg-[#0a0f1a]">AI Project Accelerator (30-Day)</option>
                  <option value="Not Sure" className="bg-[#0a0f1a]">Not Sure - Need Guidance</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Message (Optional)</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none"
                  placeholder="Tell us about your goals or any questions..."
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Book Consultation
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onConsultation }) => {
  return (
    <section className="relative py-28 px-6 overflow-hidden bg-[#0a0f1a]">
      <div className="max-w-3xl mx-auto">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[300px] bg-teal-500/[0.04] rounded-full blur-[120px]" />
        </div>

        <motion.div
          className="relative text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            Get Started
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 tracking-tight">
            Ready to Start Your{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400">AI Journey?</span>
          </h2>
          <p className="text-gray-500 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
            Join thousands of professionals transforming their careers with our expert-led AI training programs.
          </p>

          <motion.div
            className="flex flex-col sm:flex-row gap-3 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(20, 184, 166, 0.3)" }}
              whileTap={{ scale: 0.98 }}
              onClick={() => document.getElementById('programs').scrollIntoView({ behavior: 'smooth' })}
              className="px-7 py-3.5 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 text-white rounded-full font-medium text-sm tracking-wide hover:from-cyan-400 hover:via-teal-400 hover:to-emerald-400 transition-all flex items-center justify-center gap-2"
            >
              <Zap size={16} />
              Explore Programs
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onConsultation}
              className="px-7 py-3.5 border border-white/15 rounded-full text-gray-400 font-medium text-sm tracking-wide hover:text-white hover:border-teal-500/40 transition-all flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} />
              Book Consultation
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/5 py-12 px-4 bg-[#0a0f1a]">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500" />
              AI Factory
            </h3>
            <p className="text-gray-400 text-sm">Empowering the future with AI education.</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Programs</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-fundamentals" className="hover:text-teal-400 transition">Fundamentals</Link></li>
              <li><Link to="/corporate-ai-training" className="hover:text-teal-400 transition">Corporate</Link></li>
              <li><Link to="/ai-fellowship" className="hover:text-teal-400 transition">Fellowship</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="/#about" className="hover:text-teal-400 transition">About</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Blog</a></li>
              <li><a href="/#contact" className="hover:text-teal-400 transition">Contact</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition">Privacy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-600 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
            <a href="#" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
            <a href="#" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN TRAINING PAGE =============
export default function TrainingPage() {
  const [enrollmentModal, setEnrollmentModal] = useState({ isOpen: false, program: null });
  const [consultationModal, setConsultationModal] = useState(false);

  const handleEnroll = (program = null) => {
    setEnrollmentModal({ isOpen: true, program });
  };

  const handleConsultation = () => {
    setConsultationModal(true);
  };

  return (
    <div className="bg-[#0a0f1a] min-h-screen overflow-x-hidden text-white">
      <Navbar onEnroll={() => handleEnroll(null)} />
      <Hero onConsultation={handleConsultation} />
      <ProgramsSection onEnroll={handleEnroll} />
      <WhyUsSection />
      <FinalCTA onConsultation={handleConsultation} />
      <Footer />
      
      {/* Enrollment Modal */}
      <EnrollmentModal
        isOpen={enrollmentModal.isOpen}
        onClose={() => setEnrollmentModal({ isOpen: false, program: null })}
        program={enrollmentModal.program}
      />
      
      {/* Consultation Modal */}
      <ConsultationModal
        isOpen={consultationModal}
        onClose={() => setConsultationModal(false)}
      />
    </div>
  );
}